import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '#app/components/ui/accordion.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { BrandLink } from './__brand-link.tsx'
import { flattenFaqItems, type FaqCategory } from './__faq-filter.ts'
import { useReveal } from './__use-reveal.ts'

/** How many questions the home teaser previews before handing off to `/faq`. */
const TEASER_COUNT = 3

/** The accordion value rendered open on load (the first question). */
const DEFAULT_OPEN = 'faq-0'

/**
 * The home FAQ teaser (design-lane direction 1g) — a condensed two-column
 * preview that replaces the old full FAQ section and hands off to the `/faq`
 * route. Left: eyebrow + `h2` + lead + an outline "Browse all questions" CTA;
 * right: an `Accordion` of the top {@link TEASER_COUNT} questions (first open)
 * with a brand "N more answers →" link. The questions are the first N of the
 * grouped `faq.categories` Dictionary — the same single source of truth the
 * `/faq` page renders (via {@link flattenFaqItems}), so the teaser and the full
 * page never drift.
 *
 * The section keeps the `#faq` anchor so the footer sitemap / ⌘K palette /
 * scrollspy targets still resolve. Reveal-on-scroll is progressive enhancement
 * via {@link useReveal}; the resting state renders server-side (everything
 * visible), so no-JS and reduced-motion visitors get the full teaser.
 */
export function FaqTeaser() {
	const { t } = useTranslation('public')
	const ref = useRef<HTMLElement>(null)
	useReveal(ref)

	const categories = t('faq.categories', {
		returnObjects: true,
	}) as FaqCategory[]
	const items = flattenFaqItems(categories)
	const preview = items.slice(0, TEASER_COUNT)
	const moreCount = items.length - preview.length

	return (
		<section
			id="faq"
			ref={ref}
			aria-labelledby="faq-teaser-heading"
			className="container scroll-mt-20 py-24"
		>
			<div className="grid gap-12 md:grid-cols-[0.8fr_1.2fr] md:gap-16">
				<div data-reveal className="md:sticky md:top-24 md:self-start">
					<p className="text-brand text-body-xs font-semibold tracking-eyebrow uppercase">
						{t('faq.teaser.eyebrow')}
					</p>
					<h2
						id="faq-teaser-heading"
						className="tracking-h2 mt-3 text-3xl font-semibold text-balance"
					>
						{t('faq.teaser.heading')}
					</h2>
					<p className="text-muted-foreground mt-4 max-w-[34ch] text-pretty">
						{t('faq.teaser.lead')}
					</p>
					<Button asChild variant="outline" className="mt-6">
						<Link to="/faq">{t('faq.teaser.cta')}</Link>
					</Button>
				</div>

				<div data-reveal className="md:mt-2">
					<Accordion defaultValue={DEFAULT_OPEN}>
						{preview.map(({ q, a }, i) => (
							<AccordionItem key={q} value={`faq-${i}`}>
								<AccordionTrigger>{q}</AccordionTrigger>
								<AccordionContent>{a}</AccordionContent>
							</AccordionItem>
						))}
					</Accordion>
					{moreCount > 0 ? (
						<BrandLink to="/faq" className="mt-6">
							{t('faq.teaser.more', { count: moreCount })}
						</BrandLink>
					) : null}
				</div>
			</div>
		</section>
	)
}
